import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Context } from "../../components/Store";
import { useContext, useState } from "react";
import { Context2 } from "../../components/Audio";
import ReactPlayer from "react-player/lazy";
import "../../components/PracticeCircle.css";
import { createWellness } from "@/api/wellness.api";
import { useBootstrap, useFeatureToggles } from "@/features/bootstrap";
import {
  categorizedPractices,
  getWellnessActionImageSrc,
} from "@/utils/wellnessActions";
import WellnessProgressTracker from "../../components/WellnessProgressTracker";

const ActionCircle = ({ practice, color, user, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  const isMobile = window.innerWidth <= 600;

  const enlargedPractices = {
    "Help Others": "63%",
    "Imagine a peaceful place": "63%",
    "Imagine positive things in the future": "54%",
    "Squeeze my fist": "63%",
    "Give someone a high five": "63%",
    "Write in my journal": "63%",
    "Move my body": "60%",
    "Smile big": "60%",
    "Use my growth mindset": "65%",
    "Give someone a compliment": "63%",
    Stretch: "60%",
    "Create art": "63%",
    "Be mindful. Be where my feet are": "59%",
    "Count to ten taking a deep breath each time": "52%",
  };

  const imageSize = enlargedPractices[practice] ?? "55%";

    const bgColor = color === "green" ? "bg-emotion-green" : color === "blue" ? "bg-emotion-blue" : "bg-emotion-purple";
  return (
    <div
      className={`relative flex h-40 w-40 cursor-pointer flex-col items-center justify-center rounded-full sm:h-72 sm:w-72 ${bgColor}`}
      onClick={() => onSelect(practice)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img
        className="cursor-pointer object-contain px-2 pt-2 pb-0"
        src={getWellnessActionImageSrc(practice, {
          adultActionImages: user?.adultActionImages,
        })}
        alt={practice}
        style={{ width: "auto", height: imageSize }}
      />
      {isMobile && hovered && (
          <div className="pointer-events-none absolute left-1/2 top-1/2 z-[2] w-24 -translate-x-1/2 -translate-y-1/2 rounded bg-white px-2.5 py-1.5 text-center shadow-md transition duration-200 text-[15px]">
            {practice}
          </div>
        )}
      <div className="mx-3 my-2 max-w-[60%] text-center font-bold text-white line-clamp-2 text-sm sm:text-[18px] overflow-hidden text-ellipsis">
          {practice}
        </div>
    </div>
  );
};

function getColorFromEmotion(emotion) {
  if (["happy", "grateful", "calm"].includes(emotion)) {
    return "green";
  }
  if (["stressed", "scared", "angry"].includes(emotion)) {
    return "purple";
  }
  if (["sad", "tired", "lonely"].includes(emotion)) {
    return "blue";
  }
  return "green";
}

const Actions = ({
  setWellnessId,
  setAchievements,
  wellnessIdempotencyKey,
}) => {
  const navigate = useNavigate();
  const [playing, setPlaying] = useContext(Context2);
  const [wellnessDataPoint, setWellnessDataPoint] = useContext(Context);
  const [otherText, setOtherText] = useState("");
  const [otherVisible, setOtherVisible] = useState(false);
  const [otherHovered, setOtherHovered] = useState(false);
  const { data: user } = useBootstrap();
  const { otherWellnessAction } = useFeatureToggles();
  const emotion = wellnessDataPoint.emotion;
  const color = getColorFromEmotion(emotion);
  const isMobile = window.innerWidth <= 600;

  const audio_url_prefix = (user?.voice ?? "english female").replace(" ", "_");

  const saveToDatabaseAndNav = useCallback(
    async (action) => {
      const idempotencyKey = wellnessIdempotencyKey.current;
      setWellnessDataPoint((prev) => ({
        ...prev,
        action,
        idempotencyKey,
      }));

      createWellness({ action, emotion, idempotencyKey })
        .then(({ wellness, achievements }) => {
          setWellnessId(wellness._id?.toString());
          setWellnessDataPoint(wellness);
          setAchievements(achievements);
        })
        .catch((err) => console.error("[Actions] createWellness failed"));

      navigate("/performaction");
    },
    [
      emotion,
      navigate,
      setAchievements,
      setWellnessDataPoint,
      setWellnessId,
      wellnessIdempotencyKey,
    ],
  );

  const HelpText = () => {
    if (color === "green") {
      return (
        <p className="text-center text-base text-gray-600 sm:text-[18px]">
          Since you felt relaxed, <span className="font-semibold text-emotion-green">Green</span> practices help anytime.
        </p>
      );
    }
    if (color === "blue") {
      return (
        <p className="text-center text-base text-gray-600 sm:text-[18px]">
          <span className="font-semibold text-emotion-blue">Blue</span> practices help when you're feeling down.{" "}
          <span className="font-semibold text-emotion-green">Green</span> practices help anytime.
        </p>
      );
    }
    return (
      <p className="text-center text-base text-gray-600 sm:text-[18px]">
        <span className="font-semibold text-emotion-purple">Purple</span> practices help you feel less worried.{" "}
        <span className="font-semibold text-emotion-green">Green</span> practices help anytime.
      </p>
    );
  };

  return (
    <div className="grid h-screen grid-rows-[auto_1fr_auto] bg-white">
      <div className="sticky top-0 mt-10 sm:mt-0 z-10 shrink-0 bg-white sm:ml-[130px] px-0 sm:px-8 lg:px-32">
        <header className="px-4 py-4 text-center sm:px-6 sm:pb-4 sm:pt-12">
          <h1 className="mb-2 text-2xl font-bold md:text-3xl">Is there anything I need?</h1>
          <p className="mx-auto text-[15px] sm:text-base text-gray-600">Choose ONE wellness practice and try it now!</p>
        </header>
        <WellnessProgressTracker />
      </div>
      <div className="min-h-0 flex flex-col">
        <motion.div
          className="flex min-h-0 flex-1 flex-row overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="h-0 w-0 overflow-hidden opacity-0">
            <ReactPlayer
              playing={playing}
              url={"audio/" + audio_url_prefix + "/actions.wav"}
            />
          </div>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden sm:ml-[130px] px-4 sm:px-8 xl:px-32">
            <div className="mb-4 mt-4 flex min-h-0 flex-1 w-full flex-col overflow-y-auto sm:mt-4">
              <div className="flex w-full flex-wrap content-start justify-center gap-3 sm:gap-8">
                {color === "purple" &&
                  categorizedPractices.purple.map((practice) => (
                    <ActionCircle
                      key={practice}
                      practice={practice}
                      color="purple"
                      user={user}
                      onSelect={saveToDatabaseAndNav}
                    />
                  ))}
                {color === "blue" &&
                  categorizedPractices.blue.map((practice) => (
                    <ActionCircle
                      key={practice}
                      practice={practice}
                      color="blue"
                      user={user}
                      onSelect={saveToDatabaseAndNav}
                    />
                  ))}
                {categorizedPractices.green.map((practice) => (
                  <ActionCircle
                    key={practice}
                    practice={practice}
                    color="green"
                    user={user}
                    onSelect={saveToDatabaseAndNav}
                  />
                ))}
                {otherWellnessAction && !otherVisible && (
                  <div
                    className="relative flex h-40 w-40 cursor-pointer flex-col items-center justify-center rounded-full bg-emotion-green sm:h-72 sm:w-72"
                    onMouseEnter={() => setOtherHovered(true)}
                    onMouseLeave={() => setOtherHovered(false)}
                  >
                    <img
                      onClick={() => setOtherVisible(true)}
                      className="cursor-pointer object-contain px-2 pt-2 pb-0"
                      src="../images/actions/Other_BIG_FONT.png"
                      style={{ width: "auto", height: "60%" }}
                      alt="Other"
                      loading="lazy"
                    />
                    {isMobile && otherHovered && (
                      <div className="pointer-events-none absolute left-1/2 top-1/2 z-[2] w-24 -translate-x-1/2 -translate-y-1/2 rounded bg-white px-2.5 py-1.5 text-center text-[15px] shadow-md">
                        Other
                      </div>
                    )}
                    <div className="mx-3 mb-3 mt-2.5 max-w-[60%] text-center font-bold text-white line-clamp-2 text-sm sm:text-[18px]">
                      Other
                    </div>
                  </div>
                )}
              </div>
              {otherWellnessAction && otherVisible && (
                <div className="mt-3 sm:mt-8 flex w-full justify-center px-2 pb-2 sm:px-8">
                  <div className="relative w-full max-w-[90%] rounded border border-[#064E3B] bg-[#ECFDF580] p-4 sm:p-5">
                    <span className="mb-2 block text-left text-sm font-bold">
                      Other
                    </span>
                    <textarea
                      placeholder="What Do You Need?"
                      value={otherText}
                      onChange={(e) => setOtherText(e.target.value)}
                      rows={3}
                      className="mb-3 min-h-[88px] w-full resize-y rounded border bg-white px-4 py-3 placeholder-[#55555540] focus:outline-none focus:ring-2 focus:ring-white/50 sm:min-h-[100px]"
                      autoFocus
                    />
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setOtherVisible(false);
                          setOtherText("");
                        }}
                        className="rounded bg-white/20 px-4 py-2 text-sm font-bold hover:bg-white/30"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => saveToDatabaseAndNav(`Other: ${otherText.trim()}`)}
                        disabled={!otherText.trim()}
                        className="rounded bg-[#064E3B] px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
      <div className="sm:ml-[130px] px-4 pb-6 pt-2">
        <HelpText />
      </div>
    </div>
  );
};

export default Actions;
